import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Table, Tag, Typography, Switch } from 'antd'
import { Link, generatePath } from 'react-router-dom'
import _ from 'lodash'
import styled from 'styled-components'

import { Queries } from 'admin/lib/Api'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'

export function UsersList({
  searchName = '',
  searchEmail = '',
  searchMemberships = ''
}) {
  const users = Queries.useUsers()
  const userAccountMappings = Queries.useUsersAccountMappings()

  const filteredUsers = useMemo(() => {
    if (!users.isFetched || !users.data) {
      return []
    }

    let list = _(users.data)

    if (searchName) {
      list = list.filter(user =>
        user.displayName?.toLowerCase().includes(searchName.toLocaleLowerCase())
      )
    }
    if (searchEmail) {
      list = list.filter(user =>
        user.email?.toLowerCase().includes(searchEmail.toLocaleLowerCase())
      )
    }
    if (searchMemberships) {
      const searchValue = searchMemberships.toLocaleLowerCase()
      const userIds = []
      _.forOwn(userAccountMappings?.data, (accountMappings, userId) => {
        if (
          accountMappings.some(
            acc =>
              acc.name.toLowerCase().includes(searchValue) ||
              acc.projects.some(proj =>
                proj.name.toLowerCase().includes(searchValue)
              )
          )
        ) {
          userIds.push(userId)
        }
      })
      list = list.filter(user => userIds.some(uId => uId == user.userId))
    }

    return list.value()
  }, [
    users.isFetched,
    users.data,
    searchName,
    searchEmail,
    searchMemberships,
    userAccountMappings
  ])

  const columns = useMemo(
    /** @returns {import("antd/lib/table").ColumnsType<any>} */
    () => [
      {
        title: 'Name',
        dataIndex: 'displayName',
        width: '12rem',
        sorter: (a, b) => a.displayName?.localeCompare(b.displayName),
        defaultSortOrder: 'ascend'
      },
      {
        title: 'Email',
        dataIndex: 'email',
        width: '10rem',
        sorter: (a, b) => a.email?.localeCompare(b.email)
      },
      {
        title: 'Notifications Enabled',
        dataIndex: 'notificationsEnabled',
        width: '5rem',
        render: notificationsEnabled => (
          <Switch disabled={true} checked={notificationsEnabled}></Switch>
        )
      },
      {
        title: 'Memberships',
        key: 'memberships',
        render: user => {
          let accounts = userAccountMappings.data[user.userId]
          accounts = accounts ?? []
          return (
            <>
              {_(accounts)
                .sortBy(a => a.name.toLowerCase())
                .map(({ accountId, name }) => (
                  <Tag color="blue" key={accountId}>
                    {name}
                  </Tag>
                ))
                .value()}

              {_(accounts)
                .flatMap(a => a.projects)
                .uniqBy(p => p.projectId)
                .sortBy(p => p.name.toLowerCase())
                .map(({ projectId, name }) => (
                  <Tag color="volcano" key={projectId}>
                    {name}
                  </Tag>
                ))
                .value()}
            </>
          )
        }
      },
      {
        title: '',
        key: 'edit',
        width: '4rem',
        render: user => (
          <Typography.Text>
            <Link
              to={generatePath(ROUTES.USERS.EDIT, {
                userId: user.userId
              })}
            >
              Edit
            </Link>
          </Typography.Text>
        )
      }
    ],
    [userAccountMappings]
  )

  if (
    (users.isFetching && !users.data) ||
    (userAccountMappings.isFetching && !userAccountMappings.data)
  ) {
    return <ScreenSpin />
  }

  return (
    <StyledTable columns={columns} dataSource={filteredUsers} rowKey="userId" />
  )
}

UsersList.propTypes = {
  searchName: PropTypes.string,
  searchEmail: PropTypes.string,
  searchMemberships: PropTypes.string
}

const StyledTable = styled(Table)`
  width: 100%;
  height: 100%;

  /* Fill available space to ensure the page selector is in the bottom right */
  & .ant-spin-nested-loading {
    height: 100%;
  }
  & .ant-spin-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  & .ant-table {
    flex: 1 1 auto;
  }
`
